import React, { useState, useEffect } from "react";
import { ProcessingRule, ElementVersionProcessingRule, ElementVersion, DataElement } from "@/entities/all";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { FilterSelect, FilterSelectItem } from "@/components/ui/filter-select";
import DeleteProcessingRuleDialog from "./DeleteProcessingRuleDialog";
import ProcessingRuleCodeEditor from "./ProcessingRuleCodeEditor";
import ProcessingRuleAssistant from "./ProcessingRuleAssistant";
import { runEndpoint } from '@/services/flow';
import { PROCESSING_RULE_TYPES } from "@/utils/dataTypes";
import {
  Save,
  Play,
  CheckCircle,
  XCircle,
  Code,
  Settings,
  TestTube,
  Link as LinkIcon,
  Clock,
  PlusCircle,
  Trash2,
  Sparkles,
  X
} from "lucide-react";
import { toast } from "sonner";

const STATUS_CONFIG = {
  Active:     { bg: 'var(--pill-green-strong-bg)',  color: 'var(--pill-green-strong-color)' },
  Draft:      { bg: 'var(--pill-amber-bg)',         color: 'var(--pill-amber-color)' },
  Deprecated: { bg: 'var(--pill-red-bg)',           color: 'var(--pill-red-color)' },
};

const FIELD_CLASS =
  "border-2 border-nav-active bg-card-bg text-page-text focus-visible:border-nav-active-hover focus-visible:ring-0 shadow-none transition-colors";

const TABS = [
  { value: 'details',   label: 'Details',        icon: Settings },
  { value: 'assistant', label: 'Assistant',      icon: Sparkles },
  { value: 'code',      label: 'Code Editor',    icon: Code },
  { value: 'test',      label: 'Test Runner',    icon: TestTube },
  { value: 'usage',     label: null,             icon: LinkIcon }, // label set dynamically
];

export default function ProcessingRuleDetailsSheet({ ruleId, isOpen, onClose, onSaved, defaultTab = "details", onTabChange }) {
  const [rule, setRule] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [formData, setFormData] = useState({});
  const [testResults, setTestResults] = useState([]);
  const [isRunningTests, setIsRunningTests] = useState(false);
  const [usage, setUsage] = useState([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    if (ruleId && isOpen) {
      loadRule();
      loadUsage();
    }
    if (!isOpen) {
      setRule(null);
      setFormData({});
      setTestResults([]);
      setUsage([]);
    }
  }, [ruleId, isOpen]);

  const loadRule = async () => {
    setIsLoading(true);
    try {
      const data = await ProcessingRule.get(`id=${ruleId}`);
      setRule(data);
      setFormData({
        rule_name: data.rule_name || '',
        description: data.description || '',
        rule_code: data.rule_code || '',
        rule_language: data.rule_language || 'javascript',
        rule_type: data.rule_type || 'validation',
        input_type: data.input_type || 'string',
        output_type: data.output_type || 'struct',
        status: data.status || 'Active',
        test_cases: data.test_cases || []
      });
    } catch (error) {
      console.error('Error loading processing rule:', error);
      toast.error('Failed to load processing rule');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsage = async () => {
    try {
      const data = await ElementVersionProcessingRule.filter({ processing_rule_id: ruleId });
      const enrichedUsage = await Promise.all(
        data.map(async (item) => {
          try {
            const elementVersion = await ElementVersion.get(`id=${item.element_version_id}`);
            let element = null;
            if (elementVersion?.element_id) {
              element = await DataElement.get(`id=${elementVersion.element_id}`);
            }
            return { ...item, elementVersion, element };
          } catch {
            return { ...item, elementVersion: null, element: null };
          }
        })
      );
      setUsage(enrichedUsage);
    } catch (error) {
      console.error('Error loading rule usage:', error);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await ProcessingRule.update(ruleId, { ...formData, version: (rule.version || 1) + 1 });
      toast.success('Processing rule updated successfully');
      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      console.error('Error updating processing rule:', error);
      toast.error('Failed to update processing rule');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const addTestCase = () => {
    setFormData(prev => ({
      ...prev,
      test_cases: [...(prev.test_cases || []), { name: '', input: '', expected: '', description: '' }]
    }));
  };

  const updateTestCase = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      test_cases: (prev.test_cases || []).map((tc, i) => i === index ? { ...tc, [field]: value } : tc)
    }));
  };

  const removeTestCase = (index) => {
    setFormData(prev => ({
      ...prev,
      test_cases: (prev.test_cases || []).filter((_, i) => i !== index)
    }));
  };

  const runTests = async () => {
    if (!formData.test_cases?.length || !formData.rule_code) return;
    setIsRunningTests(true);
    setTestResults([]);
    try {
      const results = [];
      for (const testCase of formData.test_cases) {
        try {
          let result;
          if (formData.rule_language === 'javascript') {
            const fn = new Function('input', formData.rule_code);
            result = fn(testCase.input);
          } else if (formData.rule_language === 'python') {
            const response = await runEndpoint('cortex.execute_logic', {
              input: testCase.input, logic: formData.rule_code,
              code: formData.rule_code, language: formData.rule_language
            });
            result = response?.payload || response;
          } else {
            result = { error: `Test execution for ${formData.rule_language} is not supported in the browser.` };
          }
          results.push({
            testCase, result,
            passed: String(result?.transformedValue) === String(testCase.expected) || JSON.stringify(result) === testCase.expected,
            validationPassed: result?.isValid === true,
            outputMatched: String(result?.transformedValue) === String(testCase.expected) || JSON.stringify(result) === testCase.expected,
            executedAt: new Date().toISOString()
          });
        } catch (error) {
          results.push({
            testCase,
            result: { isValid: false, transformedValue: null, message: error.message },
            passed: false, executedAt: new Date().toISOString(), error: error.message
          });
        }
      }
      setTestResults(results);
    } catch (error) {
      console.error('Error running tests:', error);
      toast.error('Failed to run tests');
    } finally {
      setIsRunningTests(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (onTabChange) onTabChange(tab);
  };

  // Apply an assistant DRAFT into the form. Deliberately NOT a save: the draft
  // lands in the same formData the user edits by hand, so it shows up in the
  // Details / Code Editor / Test Runner tabs and still needs the sheet's own
  // "Save Changes" to reach Dolt. Two gates between a suggestion and a write.
  //
  // Fields the draft does not speak for (status, and anything a future field
  // adds) are left exactly as they were — a draft refines a rule, it does not
  // replace the row.
  const applyDraft = (draft) => {
    if (!draft) return;
    setFormData(prev => ({
      ...prev,
      rule_name: draft.rule_name || prev.rule_name,
      description: draft.description || prev.description,
      rule_code: draft.rule_code || prev.rule_code,
      rule_language: draft.rule_language || prev.rule_language,
      rule_type: draft.rule_type || prev.rule_type,
      input_type: draft.input_type || prev.input_type,
      // Drafted cases REPLACE the old ones: they were written for this code and
      // stale expectations would read as failures in the Test Runner.
      test_cases: draft.test_cases?.length ? draft.test_cases : prev.test_cases,
    }));
    // The drafted code invalidates results computed from the previous code.
    setTestResults([]);
    toast.success('Draft applied — review it, then Save Changes');
  };

  // What the rule actually runs on, so the draft matches the project's data
  // rather than a generic example. Derived from the usage rows already loaded
  // for the Usage tab — no extra request.
  const assistantContext = React.useMemo(() => {
    const elements = [...new Set(
      usage.map(u => u.element?.canonical_name).filter(Boolean)
    )].slice(0, 40);
    return elements.length ? { elements } : null;
  }, [usage]);

  const statusCfg = STATUS_CONFIG[rule?.status] || { bg: 'var(--pill-neutral-bg)', color: 'var(--pill-neutral-color)' };

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 z-40 bg-black/50 transition-opacity duration-300"
        style={{ opacity: isOpen ? 1 : 0, pointerEvents: isOpen ? 'auto' : 'none' }}
        onClick={onClose}
      />
      <div
        className="fixed inset-y-0 right-0 z-50 flex flex-col border-l shadow-xl transition-transform duration-300 ease-in-out bg-page-bg border-card-border w-full sm:w-[75vw] sm:max-w-[75vw]"
        style={{
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)'
        }}
      >
        <div className="overflow-y-auto flex-1">
          {/* Sheet header */}
          <div className="px-4 md:px-[60px] pt-[30px] pb-0">

            {/* Close button */}
            <button
              onClick={onClose}
              className="flex items-center gap-1 text-sm cursor-pointer bg-transparent border-0 p-0 mb-4 text-nav-active hover:text-nav-active-hover transition-colors"
            >
              <X size={16} />
              Close
            </button>

            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 mb-5">
              <div className="flex flex-col gap-2">
                {isLoading ? (
                  <Skeleton className="h-9 w-64" />
                ) : (
                  <h2 className="text-[32px] font-medium leading-tight text-page-title">
                    {rule?.rule_name || 'Rule Details'}
                  </h2>
                )}
                {rule && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className="inline-flex items-center gap-1 h-[18px] rounded-[9px] pl-1 pr-2"
                      style={{ background: statusCfg.bg }}
                    >
                      <span className="size-[10px] rounded-full shrink-0" style={{ background: statusCfg.color }} />
                      <span className="text-xs font-medium whitespace-nowrap leading-none" style={{ color: statusCfg.color }}>
                        {rule.status}
                      </span>
                    </span>
                    <span className="text-sm text-page-muted">Version {rule.version}</span>
                  </div>
                )}
              </div>
              {!isLoading && rule && (
                <div className="flex flex-wrap items-center gap-3 mt-1">
                  <button
                    onClick={() => setIsDeleteDialogOpen(true)}
                    className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-red-400 text-red-500 text-sm font-medium cursor-pointer bg-transparent"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer border-0 disabled:opacity-50 hover:bg-nav-active-dark transition-colors"
                  >
                    <Save size={16} />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>

            {/* Tabs */}
            {!isLoading && rule && (
              <div className="flex items-center gap-0 border-b border-card-border overflow-x-auto">
                {[
                  { value: 'details',   label: 'Details',     Icon: Settings },
                  // The assistant sits right after Details, BEFORE the code
                  // editor: for a user who cannot write the envelope by hand it
                  // is the way in, not an extra.
                  { value: 'assistant', label: 'Assistant',   Icon: Sparkles },
                  { value: 'code',      label: 'Code Editor', Icon: Code },
                  { value: 'test',      label: 'Test Runner', Icon: TestTube },
                  { value: 'usage',     label: `Usage (${usage.length})`, Icon: LinkIcon },
                ].map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    onClick={() => handleTabChange(value)}
                    className={`flex items-center gap-2 h-9 px-4 text-sm border-b-2 cursor-pointer bg-transparent border-t-0 border-l-0 border-r-0 transition-colors whitespace-nowrap shrink-0 ${activeTab === value ? 'border-nav-active text-nav-active font-medium' : 'border-transparent text-page-muted font-normal'}`}
                  >
                    <Icon size={14} />
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="px-4 md:px-[60px] py-5 pb-24 md:pb-5">
            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-64 w-full" />
              </div>
            ) : !rule ? (
              <div className="text-center py-12">
                <p className="text-sm text-page-muted">The processing rule you're looking for doesn't exist or has been deleted.</p>
              </div>
            ) : (
              <>
                {/* Details Tab */}
                {activeTab === 'details' && (
                  <div className="bg-card-bg rounded-[8px] p-5 space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <Label htmlFor="rule_name" className="text-sm font-medium text-page-text">Rule Name</Label>
                        <Input id="rule_name" value={formData.rule_name}
                          onChange={(e) => handleChange('rule_name', e.target.value)} className={`mt-1 ${FIELD_CLASS}`} />
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="description" className="text-sm font-medium text-page-text">Description</Label>
                        <Textarea id="description" value={formData.description}
                          onChange={(e) => handleChange('description', e.target.value)} rows={3} className={`mt-1 ${FIELD_CLASS}`} />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-page-text">Rule Type</Label>
                        <FilterSelect value={formData.rule_type} onValueChange={(v) => handleChange('rule_type', v)} className="mt-1 w-full">
                          <FilterSelectItem value="validation">Validation</FilterSelectItem>
                          <FilterSelectItem value="transformation">Transformation</FilterSelectItem>
                          <FilterSelectItem value="normalization">Normalization</FilterSelectItem>
                          <FilterSelectItem value="enrichment">Enrichment</FilterSelectItem>
                        </FilterSelect>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-page-text">Language</Label>
                        <FilterSelect value={formData.rule_language} onValueChange={(v) => handleChange('rule_language', v)} className="mt-1 w-full">
                          <FilterSelectItem value="javascript">JavaScript</FilterSelectItem>
                          <FilterSelectItem value="python">Python</FilterSelectItem>
                          <FilterSelectItem value="sql">SQL</FilterSelectItem>
                          <FilterSelectItem value="tiva_endpoint">Tiva Endpoint</FilterSelectItem>
                        </FilterSelect>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-page-text">Input Type</Label>
                        <FilterSelect value={formData.input_type} onValueChange={(v) => handleChange('input_type', v)} className="mt-1 w-full">
                          {PROCESSING_RULE_TYPES.map(t => (
                            <FilterSelectItem key={t} value={t}>{t}</FilterSelectItem>
                          ))}
                        </FilterSelect>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-page-text">Status</Label>
                        <FilterSelect value={formData.status} onValueChange={(v) => handleChange('status', v)} className="mt-1 w-full">
                          <FilterSelectItem value="Active">Active</FilterSelectItem>
                          <FilterSelectItem value="Draft">Draft</FilterSelectItem>
                          <FilterSelectItem value="Deprecated">Deprecated</FilterSelectItem>
                        </FilterSelect>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 pt-4 text-sm border-t border-card-border text-page-muted">
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        Created: {rule.created_date ? new Date(rule.created_date).toLocaleDateString() : 'N/A'}
                      </div>
                      <div>Updated: {rule.updated_date ? new Date(rule.updated_date).toLocaleDateString() : 'N/A'}</div>
                    </div>
                  </div>
                )}

                {/* Assistant Tab — plain-language authoring of the rule. */}
                {activeTab === 'assistant' && (
                  <ProcessingRuleAssistant
                    current={formData}
                    context={assistantContext}
                    onApply={applyDraft}
                  />
                )}

                {/* Code Editor Tab */}
                {activeTab === 'code' && (
                  <div className="bg-card-bg rounded-[8px] p-5">
                    <div className="mb-4">
                      <h3 className="text-base font-medium text-page-text">Rule Code</h3>
                      <p className="text-sm mt-0.5 text-page-muted">Edit the processing logic for this rule</p>
                    </div>
                    <ProcessingRuleCodeEditor
                      code={formData.rule_code}
                      onChange={(value) => handleChange('rule_code', value)}
                      language={formData.rule_language}
                      rows={20}
                    />
                  </div>
                )}

                {/* Test Runner Tab */}
                {activeTab === 'test' && (
                  <div className="bg-card-bg rounded-[8px] p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-base font-medium text-page-text">Test Cases</h3>
                        <p className="text-sm mt-0.5 text-page-muted">Define and run test cases to validate the rule</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={addTestCase}
                          aria-label="Add Test Case"
                          className="flex items-center justify-center gap-2 h-9 w-9 sm:w-auto sm:px-4 rounded-[5px] border-2 border-nav-active text-nav-active text-sm font-medium cursor-pointer bg-card-bg hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
                        >
                          <PlusCircle size={24} />
                          <span className="hidden sm:inline">Add Test Case</span>
                        </button>
                        <button
                          onClick={runTests}
                          disabled={isRunningTests || !formData.test_cases?.length || !formData.rule_code}
                          aria-label="Run All Tests"
                          className="flex items-center justify-center gap-2 h-9 w-9 sm:w-auto sm:px-4 rounded-[5px] text-white text-sm font-medium cursor-pointer border-0 disabled:opacity-50 transition-colors bg-[var(--pill-green-strong-color)]"
                        >
                          <Play size={24} />
                          <span className="hidden sm:inline">{isRunningTests ? 'Running...' : 'Run All Tests'}</span>
                        </button>
                      </div>
                    </div>

                    {(!formData.test_cases || formData.test_cases.length === 0) ? (
                      <div className="text-center py-8 text-sm rounded-[8px] text-page-muted border-2 border-dashed border-card-border">
                        No test cases defined. Add test cases to validate your processing rule.
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {formData.test_cases.map((testCase, index) => {
                          const testResult = testResults.find(r => r.testCase === testCase);
                          return (
                            <div key={index} className="p-4 rounded-[8px] space-y-3 bg-table-stripe border border-card-border">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-page-text">Test Case {index + 1}</span>
                                  {testResult && (
                                    testResult.passed ? (
                                      <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] px-2 text-xs font-medium bg-[var(--pill-green-strong-bg)] text-pill-green">
                                        <CheckCircle size={10} />Passed
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] px-2 text-xs font-medium bg-[var(--pill-red-bg)] text-error-text">
                                        <XCircle size={10} />Failed
                                      </span>
                                    )
                                  )}
                                </div>
                                <button
                                  onClick={() => removeTestCase(index)}
                                  className="flex items-center justify-center w-7 h-7 rounded-[4px] cursor-pointer border-0 bg-transparent text-nav-hover hover:text-nav-active transition-colors"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>

                              <div>
                                <Label className="text-xs text-page-muted">Test Name</Label>
                                <Input value={testCase.name || ''} onChange={(e) => updateTestCase(index, 'name', e.target.value)}
 className={`mt-1 ${FIELD_CLASS}`} />
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-xs text-page-muted">Input</Label>
                                  <Input value={testCase.input || ''} onChange={(e) => updateTestCase(index, 'input', e.target.value)}
                                    placeholder="Test input value" className={`mt-1 font-mono ${FIELD_CLASS}`} />
                                </div>
                                <div>
                                  <Label className="text-xs text-page-muted">Expected Output</Label>
                                  <Input value={testCase.expected || ''} onChange={(e) => updateTestCase(index, 'expected', e.target.value)}
                                    placeholder="Expected result" className={`mt-1 font-mono ${FIELD_CLASS}`} />
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs text-page-muted">Description</Label>
                                <Input value={testCase.description || ''} onChange={(e) => updateTestCase(index, 'description', e.target.value)}
                                  placeholder="What this test verifies" className={`mt-1 ${FIELD_CLASS}`} />
                              </div>

                              {testResult && (
                                <div className="p-3 rounded-[8px] text-sm"
                                  style={testResult.passed
                                    ? { background: 'var(--pill-green-strong-bg)', border: '1px solid var(--pill-green-color)' }
                                    : { background: 'var(--pill-red-bg)', border: '1px solid var(--pill-red-color)' }}>
                                  <div className="font-medium mb-1 flex items-center gap-2" style={{ color: testResult.passed ? 'var(--pill-green-strong-color)' : 'var(--pill-red-dark)' }}>
                                    {testResult.passed ? 'Test Passed' : 'Test Failed'}
                                    {!testResult.passed && testResult.validationPassed && !testResult.outputMatched && (
                                      <span className="text-xs font-normal text-[var(--pill-amber-color)]">— output doesn't match expected</span>
                                    )}
                                  </div>
                                  {testResult.error ? (
                                    <p className="text-[var(--pill-red-dark)]">{testResult.error}</p>
                                  ) : (
                                    <pre className="text-xs font-mono overflow-x-auto text-page-text">
                                      Result: {JSON.stringify(testResult.result, null, 2)}
                                    </pre>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {testResults.length > 0 && (
                      <div className="pt-4 mt-4 flex items-center gap-4 text-sm border-t border-card-border">
                        <span className="font-medium text-page-text">Results:</span>
                        <span className="text-pill-green">{testResults.filter(r => r.passed).length} passed</span>
                        <span className="text-error-text">{testResults.filter(r => !r.passed).length} failed</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Usage Tab */}
                {activeTab === 'usage' && (
                  <div className="bg-card-bg rounded-[8px] p-5">
                    <div className="mb-4">
                      <h3 className="text-base font-medium text-page-text">Rule Usage</h3>
                      <p className="text-sm mt-0.5 text-page-muted">Data elements and versions that use this processing rule</p>
                    </div>
                    {usage.length === 0 ? (
                      <p className="text-sm text-center py-8 text-page-muted">
                        This rule is not currently used by any element versions.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {usage.map((item, i) => {
                          const evStatus = item.elementVersion?.status;
                          const evCfg = evStatus === 'APPROVED'
                            ? { bg: 'var(--pill-green-strong-bg)', color: 'var(--pill-green-strong-color)' }
                            : evStatus === 'DRAFT'
                            ? { bg: 'var(--pill-amber-bg)', color: 'var(--pill-amber-color)' }
                            : { bg: 'var(--pill-neutral-bg)', color: 'var(--pill-neutral-color)' };
                          const activeCfg = item.is_active
                            ? { bg: 'var(--pill-green-strong-bg)', color: 'var(--pill-green-strong-color)' }
                            : { bg: 'var(--pill-neutral-bg)', color: 'var(--pill-neutral-color)' };
                          return (
                            <div
                              key={item.id}
                              className="flex items-center justify-between p-4 rounded-[8px]"
                              style={{ background: i % 2 === 0 ? 'var(--table-stripe)' : 'var(--card-bg)', border: '1px solid var(--card-border)' }}
                            >
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <a
                                    href={`/element-details?id=${item.elementVersion?.element_id}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="font-medium hover:underline text-nav-active"
                                  >
                                    {item.element?.canonical_name || `Element #${item.elementVersion?.element_id || 'Unknown'}`}
                                  </a>
                                </div>
                                <div className="flex items-center gap-3 text-sm text-page-muted">
                                  <a
                                    href={`/element-details?id=${item.elementVersion?.element_id}&version=${item.element_version_id}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="hover:underline text-page-muted"
                                  >
                                    Version {item.elementVersion?.version_number || item.element_version_id}
                                  </a>
                                  <span>•</span>
                                  <span>Pipeline position: {item.execution_order + 1}</span>
                                  {evStatus && (
                                    <>
                                      <span>•</span>
                                      <span className="inline-flex items-center gap-1 h-[16px] rounded-[8px] pl-0.5 pr-2"
                                        style={{ background: evCfg.bg }}>
                                        <span className="size-[8px] rounded-full shrink-0" style={{ background: evCfg.color }} />
                                        <span className="text-xs font-medium" style={{ color: evCfg.color }}>{evStatus}</span>
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] pl-1 pr-2"
                                style={{ background: activeCfg.bg }}>
                                <span className="size-[10px] rounded-full shrink-0" style={{ background: activeCfg.color }} />
                                <span className="text-xs font-medium" style={{ color: activeCfg.color }}>
                                  {item.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <DeleteProcessingRuleDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onRuleDeleted={() => {
          setIsDeleteDialogOpen(false);
          if (onSaved) onSaved();
          onClose();
        }}
        rule={rule ? { ...rule, usage_count: usage.length } : null}
      />
    </>
  );
}
